import { FlexBox, Spinner, Text } from '@dixa/lib-shared-habla';
import { User } from 'lib-shared-types-api';
import { PropsWithChildren } from 'lib-shared-types-utils';
import { useEffect } from 'react';
import { useUpdateEffect } from 'react-use';
import { Waypoint } from 'react-waypoint';

import { searchApi } from 'apis/search';
import { usePaginatedEndpoint } from 'utils/apis/pagination/usePaginatedEndpoint';
import { pubSub, PubSubSubscriptionDispose } from 'utils/pubSub';
import { useAppPathParams } from 'utils/url/useAppPathParams';
import { useAppSearchParams } from 'utils/url/useAppSearchParams';

import { ContactsUser } from './components/user/contactsUser';

function ContactListMeta({ children }: PropsWithChildren) {
  return (
    <FlexBox justifyContent="center" alignItems="center" style={{ height: 72 }}>
      {children}
    </FlexBox>
  );
}

export function ContactsUsers(): JSX.Element {
  const { id } = useAppPathParams('/contacts/user/:id');

  const {
    params: { q: searchTerm = '' },
  } = useAppSearchParams('/contacts/user/:id');

  const {
    items: contacts,
    asyncStatus,
    nextPage,
    refetchItems,
    search,
    hasNextPage,
    updateItem,
  } = usePaginatedEndpoint({
    endpoint: searchApi.endpoints.readSearchUser,
    itemsSelector: ({ data }) => data.results.hits,
    infinite: true,
    initialArg: { limit: 50, offset: 0 },
  });

  useUpdateEffect(() => {
    search(searchTerm);
  }, [searchTerm, search]);

  useEffect(() => {
    const unsubscribe = pubSub.subscribe('userModalUserAdded', () => {
      // Delaying the refetch to give ElasticSearch some time to index the new user.
      setTimeout(() => {
        refetchItems();
      }, 2000);
    });

    return () => unsubscribe();
  }, [refetchItems]);

  useEffect(() => {
    const updateUser = (user: User) => {
      const contact = contacts.find((contact) => contact.id === user.id);

      if (contact) {
        updateItem(
          {
            ...contact,
            email: user.email ?? undefined,
            phone_number: user.phone_number ?? undefined,
            display_name: user.name ?? undefined,
            company_id: user.company_id ?? undefined,
            avatar_url: user.avatar_url ?? undefined,
            user_type: user.type,
          },
          (a, b) => a.id === b.id,
        );
      }
    };

    const subscriptions: PubSubSubscriptionDispose[] = [
      pubSub.subscribe('userModalUserUpdated', updateUser),
      pubSub.subscribe('mergeContactsModalUserMerged', updateUser),
    ];

    return () => {
      subscriptions.forEach((unsubscribe) => unsubscribe());
    };
  }, [contacts, updateItem]);

  if (asyncStatus === 'rejected') {
    return (
      <ContactListMeta>
        <Text.Paragraph>
          An error occurred while getting users list
        </Text.Paragraph>
      </ContactListMeta>
    );
  }

  if (asyncStatus === 'pending' && contacts.length === 0) {
    return (
      <ContactListMeta>
        <Spinner />
      </ContactListMeta>
    );
  }

  return (
    <>
      {contacts.map((user) => (
        <ContactsUser key={user.id} user={user} selected={user.id === id} />
      ))}

      {asyncStatus === 'pending' && (
        <ContactListMeta>
          <Spinner />
        </ContactListMeta>
      )}

      {asyncStatus === 'fulfilled' && hasNextPage && (
        <Waypoint onEnter={nextPage} />
      )}
    </>
  );
}
